import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';

const polarToCartesian = (centerX, centerY, radius, angle) => ({
  x: centerX + (Math.cos(angle) * radius),
  y: centerY + (Math.sin(angle) * radius)
});

const buildSelectorState = (rootDataset, currentId) => {
  const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
  if (nextDefault && !rootDataset?.views?.[currentId]) {
    return nextDefault;
  }
  return currentId;
};

const wrapLabel = (label) => {
  return String(label || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 3);
};

const RadarVisual = ({ model }) => {
  useUiLanguage();
  const rootDataset = model.dataset;
  const [selectedViewId, setSelectedViewId] = useState(rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '');

  useEffect(() => {
    setSelectedViewId((currentId) => {
      const nextDefault = rootDataset?.selectedViewId || rootDataset?.selectors?.[0]?.id || '';
      if (!nextDefault) {
        return currentId;
      }
      if (!rootDataset?.views?.[currentId] || currentId !== nextDefault) {
        return nextDefault;
      }
      return buildSelectorState(rootDataset, currentId);
    });
  }, [rootDataset]);

  const dataset = rootDataset?.views?.[selectedViewId] || rootDataset;
  const axes = dataset?.axes || [];
  const profiles = dataset?.profiles || [];
  const orderedProfiles = useMemo(
    () => [
      ...profiles.filter((profile) => profile.id === 'org_average'),
      ...profiles.filter((profile) => profile.id !== 'org_average')
    ],
    [profiles]
  );

  const geometry = useMemo(() => {
    const centerX = 180;
    const centerY = 148;
    const radius = 98;
    return axes.map((axis, index) => {
      const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / Math.max(axes.length, 1));
      const outer = polarToCartesian(centerX, centerY, radius, angle);
      const labelPosition = polarToCartesian(centerX, centerY, radius + 22, angle);
      return { ...axis, angle, outer, labelPosition };
    });
  }, [axes]);

  if (!axes.length || !orderedProfiles.length) return null;

  if ((model.size || 'medium') === 'compact') {
    const primaryProfile = orderedProfiles[0];
    const rankedAxes = axes
      .map((axis) => ({
        ...axis,
        value: Number(primaryProfile.values?.[axis.id] || 0)
      }))
      .sort((left, right) => right.value - left.value);

    return (
      <div className="analytics-visual analytics-visual-scatter-list">
        {rootDataset?.selectors?.length ? (
          <div className="analytics-visual-compact-select-wrap">
            <select
              className="analytics-visual-compact-select"
              aria-label={rootDataset.selectorLabel || uiText("Selector")}
              value={selectedViewId}
              onChange={(event) => setSelectedViewId(event.target.value)}
            >
              {rootDataset.selectors.map((option) => (
                <option key={option.id} value={option.id}>
                  {uiText(option.label)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {rankedAxes.map((axis) => (
          <div key={axis.id} className="analytics-visual-scatter-list-row">
            <div className="analytics-visual-scatter-list-name">
              <span className={`analytics-visual-legend-swatch ${primaryProfile.tone || 'accent'}`} />
              <span className="analytics-visual-label">{uiText(axis.label)}</span>
            </div>
            <div className="analytics-visual-scatter-list-metrics">
              <strong>{axis.value.toFixed(1)}</strong>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`analytics-visual analytics-visual-radar ${model.size || 'medium'}`}>
      {rootDataset?.selectors?.length ? (
        <div className="analytics-visual-source-picker" role="tablist" aria-label={rootDataset.selectorLabel || uiText("Selector")}>
          {rootDataset.selectors.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`analytics-visual-source-pill${selectedViewId === option.id ? ' active' : ''}`}
              onClick={() => setSelectedViewId(option.id)}
            >
              {uiText(option.label)}
            </button>
          ))}
        </div>
      ) : null}

      <svg viewBox="0 0 360 308" className="analytics-visual-radar-svg" role="img" aria-label={dataset.title}>
        {[0.25, 0.5, 0.75, 1].map((step) => {
          const ringPoints = geometry
            .map((axis) => {
              const point = polarToCartesian(180, 148, 98 * step, axis.angle);
              return `${point.x},${point.y}`;
            })
            .join(' ');
          return <polygon key={step} points={ringPoints} className="analytics-visual-radar-ring" />;
        })}

        {geometry.map((axis) => (
          <g key={axis.id}>
            <line x1="180" y1="148" x2={axis.outer.x} y2={axis.outer.y} className="analytics-visual-radar-axis" />
            <text
              x={axis.labelPosition.x}
              y={axis.labelPosition.y}
              textAnchor={axis.labelPosition.x > 170 ? 'start' : axis.labelPosition.x < 130 ? 'end' : 'middle'}
              className="analytics-visual-axis-label analytics-visual-radar-axis-label"
            >
              {wrapLabel(axis.label).map((line, index) => (
                <tspan key={`${axis.id}-${index}`} x={axis.labelPosition.x} dy={index === 0 ? 0 : 16}>
                  {line}
                </tspan>
              ))}
            </text>
          </g>
        ))}

        {orderedProfiles.map((profile) => {
          const polygon = geometry.map((axis) => {
            const maximum = Number(axis.maximum || 100);
            const value = Number(profile.values?.[axis.id] || 0);
            const point = polarToCartesian(180, 148, (Math.max(0, Math.min(value, maximum)) / Math.max(maximum, 1)) * 98, axis.angle);
            return `${point.x},${point.y}`;
          }).join(' ');

          return (
            <g key={profile.id}>
              <polygon points={polygon} className={`analytics-visual-radar-shape ${profile.tone || 'accent'}`} />
              <polyline points={`${polygon} ${polygon.split(' ')[0]}`} className={`analytics-visual-radar-line ${profile.tone || 'accent'}`} />
            </g>
          );
        })}
      </svg>

      <div className="analytics-visual-legend">
        {orderedProfiles.map((profile) => (
          <div key={profile.id} className="analytics-visual-legend-item">
            <span className={`analytics-visual-legend-swatch ${profile.tone || 'accent'}`} />
            <span className="analytics-visual-sub">{uiText(profile.label)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RadarVisual;
